import { t } from "@lingui/macro";
import cx from "classnames";
import { Field, Form, Formik } from "formik";
import MultiRangeSlider from "multi-range-slider-react";
import React, { useState } from "react";
import InputRange from "react-input-range";
import { useDispatch, useSelector } from "react-redux";
import { GetCoreUsers } from "../../actionTypes/coreUsers";
import { GetDesiredPlaces } from "../../actionTypes/desiredPlaces";
import { GetResponsibilities } from "../../actionTypes/responsibilities";
import { CreateTalent } from "../../actionTypes/talents";
import Yup from "../../components/App/Yup";
import Input from "../../components/Form/Formik/Input";
import { Select } from "../../components/Form/Formik/Select";
import { ResponsibilitySelect } from "../../components/ResponsibilitySelect";
import { useApiSelector, useGetResourceHook, usePrevious } from "../../components/Utils/hook";
import LoaderComp from "../../components/Utils/Loader";
import { ApiError } from "../../interfaces/api/error";
import { ApiSuccess } from "../../interfaces/api/success";
import { Violation } from "../../interfaces/api/violation";
import { CoreUser } from "../../interfaces/resources/coreUser";
import { DesiredPlace, desiredPlacesRemote } from "../../interfaces/resources/desiredPlace";
import {
  formatResponsibilities,
  Responsibility,
  ResponsibilityWithSeniority,
} from "../../interfaces/resources/responsibility";
import { State } from "../../interfaces/state";
import { getCoreUsersAction } from "../../services/api/coreUser/actions";
import { getDesiredPlacesAction } from "../../services/api/desiredPlaces/actions";
import { extractIdFromIri } from "../../services/api/helpers";
import { getResponsibilitiesAction } from "../../services/api/responsibilities/actions";
import { createTalentAction } from "../../services/api/talent/actions";
import globalStyles from "../../styles/global.module.scss";
import styles from "./index.module.scss";

const formName = "createTalent";

export const TalentCreatePage: React.FC = () => {
  const { apiErrors, apiSuccess, apiPendingRequests } = useApiSelector();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: State) => state.currentUser);
  const responsibilities = useSelector((state: State) => state.responsibilities);
  const coreUsers = useSelector((state: State) => state.coreUsers);

  // Local UI state for loader and messages.
  const [loader, setLoader] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [expValue, setExpValue] = React.useState<number>(0);
  const defaultMinSlryValue = 30;
  const defaultMaxSlryValue = 53;
  const [minSlryValue, setMinSlryValue] = React.useState<number | undefined>(undefined);
  const [maxSlryValue, setMaxSlryValue] = React.useState<number | undefined>(undefined);
  const desiredPlacesState = useSelector((state: State) => state.desiredPlaces);
  const desiredPlaces = desiredPlacesState.filter((e) => !desiredPlacesRemote.includes(e.value));
  const remotePreferences = desiredPlacesState.filter((e) => desiredPlacesRemote.includes(e.value));
  const [errors, setErrors] = useState<Violation[]>([]);
  const [error, setError] = useState<boolean | string>(false);
  const [selected, setSelected] = React.useState<ResponsibilityWithSeniority[] | null>([]);
  const [success, setSuccess] = useState(false);
  const [isDisabled, setIsDisabled] = React.useState(false);
  const prevApiSuccess = usePrevious(apiSuccess);
  const prevApiErrors = usePrevious(apiErrors);

  // Validation: URL must be valid and email must be a valid email.
  const validationSchema = Yup.object().shape({
    url: Yup.string().url("Veuillez saisir une URL valide").required("L'URL LinkedIn est obligatoire"),

    email: Yup.string().email("Veuillez saisir un email valide").required("L'email est obligatoire"),

    tp: Yup.object().shape({
      coreResponsibility: Yup.object().nullable().required("La responsabilité principale est obligatoire"),

      desiredPlaces: Yup.array()
        .of(
          Yup.object().shape({
            "@id": Yup.string().required(),
            value: Yup.string().required(),
          }),
        )
        .min(1, "Veuillez sélectionner au moins un lieu souhaité")
        .required("Les lieux souhaités sont obligatoires"),

      remotePreferences: Yup.array()
        .of(
          Yup.object().shape({
            "@id": Yup.string().required(),
            value: Yup.string().required(),
          }),
        )
        .min(1, "Veuillez sélectionner au moins une préférence de télétravail")
        .required("Les préférences de télétravail sont obligatoires"),
    }),
  });

  const getResponsibilities = (): void => {
    dispatch(getResponsibilitiesAction());
  };

  const getCoreUsers = (): void => {
    dispatch(getCoreUsersAction());
  };

  useGetResourceHook(
    apiErrors,
    apiPendingRequests,
    apiSuccess,
    GetResponsibilities,
    responsibilities,
    getResponsibilities,
  );
  useGetResourceHook(apiErrors, apiPendingRequests, apiSuccess, GetCoreUsers, coreUsers, getCoreUsers);

  const getDesiredPlaces = (): void => {
    dispatch(getDesiredPlacesAction());
  };
  useGetResourceHook(apiErrors, apiPendingRequests, apiSuccess, GetDesiredPlaces, desiredPlaces, getDesiredPlaces);

  const initialValues = {
    url: "",
    email: "",
    tim: currentUser ? currentUser : null,
    tp: {
      coreResponsibility: null,
      desiredPlaces: [],
      remotePreferences: [],
      responsibilities: [],
      seniority: 0,
      minSalary: 0,
      maxSalary: 0,
    },
  };
  const handleSelectedRespChange = (result: ResponsibilityWithSeniority[]): void => setSelected(result);

  const createTalent = (values: any) => {
    const sortByIri = (a: any, b: any) => {
      const aId = extractIdFromIri(a["@id"]);
      const bId = extractIdFromIri(b["@id"]);

      return aId - bId;
    };
    const vals = {
      ...values,
      desiredPlaces: values.tp.desiredPlaces.sort(sortByIri),
      remotePreferences: (values as any).tp.remotePreferences.sort(sortByIri),
    } as any;
    if (currentUser) {
      const tp = {
        dPlaces: [...vals.desiredPlaces, ...vals.remotePreferences],
        coreResponsibility: values.tp.coreResponsibility,
        responsibilities: selected,
        experience: expValue,
        expectedRemuneration: {
          fixedSalary: minSlryValue ? minSlryValue : defaultMinSlryValue,
          fullPackage: maxSlryValue ? maxSlryValue : defaultMaxSlryValue,
        },
      };

      dispatch(createTalentAction(values.url, values.email, currentUser, tp, values.tim["@id"]));
    }
  };

  // When the form is submitted, dispatch the scraping call for the provided URL.
  // You can adjust this logic if you need to incorporate the email in the API call.
  const handleSubmit = async (values: any) => {
    setLoader(true);
    try {
      // The scrapin action is re-used. If you need to use the email parameter, you can pass it as second argument or merge it in the payload.
      createTalent(values);
      setLoader(false);
      setSuccessMessage("Données récupérées avec succès !");
    } catch (e: any) {
      setLoader(false);
      setErrorMessage("Echec de la récupération de données.");
    }
  };

  // Automatically clear messages after a delay
  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (successMessage || errorMessage) {
      timer = setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);
  React.useEffect(() => {
    const senioritiesMissing = !!selected && selected.some((s) => !!!s.seniority);
    setIsDisabled(!!!selected?.length || senioritiesMissing);
  }, [selected]);

  React.useEffect(() => {
    if (
      prevApiSuccess
      && !prevApiSuccess.some((e: ApiSuccess) => formName === e.form)
      && apiSuccess
      && apiSuccess.some((e: ApiSuccess) => formName === e.form)
    ) {
      setLoader(false);
      setError(false);
      setErrors([]);
      setSuccess(true);
    }
  }, [apiSuccess, prevApiSuccess, setError, setErrors, setSuccess]);

  React.useEffect(() => {
    if (
      (!prevApiErrors || !prevApiErrors.some((e: ApiError) => formName === e.form))
      && apiErrors
      && apiErrors.some((e: ApiError) => formName === e.form)
    ) {
      const apiError = apiErrors.find((e: ApiError) => formName === e.form);
      if (apiError) {
        setErrors(apiError.payload.violations || []);
        setError(apiError.payload.message);
        setSuccess(false);
      }
    }
  }, [apiErrors, prevApiErrors, setError, setErrors, setSuccess]);

  React.useEffect(() => {
    if (apiPendingRequests.some((e) => e.type === CreateTalent.REQUEST)) {
      setLoader(true);
    } else {
      setLoader(false);
    }
  }, [apiPendingRequests]);

  return (
    <div className={styles.pageContainer}>
      {loader && (
        <div className={styles.loaderContainer}>
          <LoaderComp />
        </div>
      )}

      {successMessage && <div className={styles.successMessage}>{successMessage}</div>}
      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

      <div className={cx(styles.formContainer, { [styles.blurActive]: loader })}>
        <div className={styles.titleContainer}>
          <h1>{t({ id: "talentCreatePage.title", message: "Créer un nouveau Talent" })}</h1>
        </div>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {(form) => (
            <Form className={styles.form}>
              <div className={styles.formGroup}>
                <Field
                  name="url"
                  component={Input}
                  type="text"
                  label={t({ id: "talentCreatePage.linkedinUrl.label", message: "URL LinkedIn" })}
                  placeholder="https://linkedin.com/..."
                  required={true}
                />
              </div>
              <div className={styles.formGroup}>
                <Field
                  name="email"
                  component={Input}
                  type="email"
                  label={t({ id: "talentCreatePage.email.label", message: "Email Talent" })}
                  placeholder="talent@example.com"
                  required={true}
                />
              </div>
              <div className={styles.formGroup}>
                <Field
                  name="tim"
                  component={Select}
                  isMulti={false}
                  isSearchable={true}
                  label="Talent Manager"
                  placeholder="Nom du TIM"
                  getOptionLabel={(coreUser: CoreUser): string => `${coreUser.firstname} ${coreUser.lastname}`}
                  getOptionValue={(coreUser: CoreUser): string => coreUser["@id"]}
                  isLoading={apiPendingRequests.some((item) => GetCoreUsers.REQUEST === item.type)}
                  options={coreUsers.filter((e) => e.enabled)}
                  required={true}
                />
              </div>
              <div className={styles.profileContainer}>
                <div className={styles.titleContainer}>
                  <h1>Profil Candidat</h1>
                </div>
                <div className={styles.formGroup}>
                  <Field
                    name="tp.coreResponsibility"
                    component={Select}
                    isMulti={false}
                    isSearchable={true}
                    label={t`CandidateProfileCard.coreResponsibility.label`}
                    placeholder={t`talentProfile.responsibilities.placeholder`}
                    getOptionLabel={(option: Responsibility): string => option.value}
                    getOptionValue={(option: Responsibility): string => option["@id"]}
                    isLoading={apiPendingRequests.some((item) => GetResponsibilities.REQUEST === item.type)}
                    options={formatResponsibilities(responsibilities)}
                  />
                  {form.touched.tp?.coreResponsibility && form.errors.tp?.coreResponsibility && (
                    <div className="error">{form.errors.tp?.coreResponsibility}</div>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <div>
                    <label className={styles.formLabel}>Niveau d&apos;expérience totale</label>
                  </div>
                  <div className={styles.inputRangeContainer}>
                    <InputRange
                      maxValue={16}
                      minValue={0}
                      step={1}
                      formatLabel={(value: number): string => {
                        return value === 0
                          ? "< 1 an"
                          : value === 1
                          ? `${value} an`
                          : value === 16
                          ? "+15 ans"
                          : `${value} ans`;
                      }}
                      value={expValue}
                      onChange={(val): void => {
                        setExpValue(val as number);
                      }}
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <div>
                    <label className={styles.formLabel}>Salaire souhaité</label>
                  </div>
                  <div className={styles.inputRangeContainer}>
                    <MultiRangeSlider
                      min={0}
                      max={150}
                      step={1}
                      minValue={defaultMinSlryValue}
                      maxValue={defaultMaxSlryValue}
                      labels={["0k", "150k"]}
                      ruler={false}
                      onChange={(e): void => {
                        setMinSlryValue(e.minValue);
                        setMaxSlryValue(e.maxValue);
                      }}
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <Field
                    name="tp.desiredPlaces"
                    component={Select}
                    getOptionLabel={(item: DesiredPlace): string => item.value}
                    getOptionValue={(item: DesiredPlace): string => item["@id"]}
                    isLoading={apiPendingRequests.some((e) => e.type === GetDesiredPlaces.REQUEST)}
                    isSearchable={true}
                    isClearable={false}
                    isMulti={true}
                    label={t`CandidateProfileCard.desiredPlaces.label`}
                    options={desiredPlaces}
                    violation={errors}
                  />
                </div>
                <div className={styles.formGroup}>
                  <Field
                    name="tp.remotePreferences"
                    component={Select}
                    getOptionLabel={(item: DesiredPlace): string => item.value}
                    getOptionValue={(item: DesiredPlace): string => item["@id"]}
                    isLoading={apiPendingRequests.some((e) => e.type === GetDesiredPlaces.REQUEST)}
                    isSearchable={true}
                    isClearable={false}
                    isMulti={true}
                    label={t`CandidateProfileCard.remotePreferences.label`}
                    options={remotePreferences}
                    violation={errors}
                  />
                </div>
                {responsibilities.length > 0 && selected ? (
                  <div className={styles.formGroup}>
                    <ResponsibilitySelect
                      onChange={handleSelectedRespChange}
                      options={responsibilities}
                      value={selected}
                    />
                  </div>
                ) : null}
                {success || error ? (
                  <div className={styles.messagesContainer}>
                    {success ? (
                      <div className={cx(styles.message, styles.successMessage)}>
                        <h4>{t`EditCandidateProfileModal.success.header`}</h4>
                        <p>{t`EditCandidateProfileModal.success.content`}</p>
                      </div>
                    ) : null}
                    {error ? (
                      <div className={cx(styles.message, styles.errorMessage)}>
                        <h4>{t`EditCandidateProfileModal.error.header`}</h4>
                        <p>
                          {t`EditCandidateProfileModal.error.content`} {error}
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <button
                type="submit"
                className={cx(styles.formSubmitCta, globalStyles.cta, globalStyles.primaryCta)}
                disabled={isDisabled}
              >
                {t({ id: "talentCreatePage.submit", message: "Créer Talent" })}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
